/**
 * Vino Cammino six-pack badge - live updates (display-only guidance layer).
 *
 * Mirrors the Minmaxify rule: checkout requires the qualifying bottle count
 * to be a positive multiple of 6. This script never blocks or alters
 * checkout - Minmaxify enforces; this only informs.
 *
 * Initial render is done server-side by snippets/vc-six-badge.liquid; this
 * script re-fetches /cart.js on every cart change and re-renders the header
 * badge and every in-cart status block so all surfaces always agree.
 *
 * The wine-type list lives in snippets/vc-six-badge.liquid (emitted as
 * data-wine-types on the header badge) so Liquid and JS cannot drift; the
 * constant below is only a fallback. Keep both in sync with Minmaxify.
 *
 * Pack rule: product type containing "pack" counts as 6 bottles per unit.
 * Only safe while every `Pack` product is a six-bottle case. Any pack type
 * whose bottle count is not a multiple of 6 must be counted by its real
 * number, never rounded.
 * TODO: when the 3-pack returns, give it a distinct product type and add a
 * "counts as 3" rule here and in snippets/vc-six-badge.liquid.
 */
(function () {
  'use strict';

  var FALLBACK_WINE_TYPES = ['red', 'white', 'sparkling', 'cognac', 'grappa', 'sweet'];
  var RING_CIRCUMFERENCE = 131.9469; // 2 * PI * r, r = 21 (matches the snippet)
  var EXC_LINE = "Accessories, magnums and gift cards can ship in any number and don't count toward the six bottles.";
  var PACK_LINE = 'Each curated pack counts as its six bottles.';
  var NO_WINE_LINE = 'Gift cards, magnums and accessories ship on their own — no six-bottle minimum.';

  function wineTypes() {
    var badge = document.querySelector('[data-vc-badge]');
    if (badge && badge.dataset.wineTypes) {
      return badge.dataset.wineTypes.split(',');
    }
    return FALLBACK_WINE_TYPES;
  }

  // Single source of truth for the maths (same as the Liquid snippet).
  function computeState(items) {
    var types = wineTypes();
    var q = 0;
    var hasExtras = false;
    var hasPack = false;

    (items || []).forEach(function (item) {
      var type = (item.product_type || '').toLowerCase();
      var qty = item.quantity || 0;
      var isWine = types.some(function (w) {
        return w && type.indexOf(w) !== -1;
      });
      if (isWine) {
        q += qty;
      } else if (type.indexOf('pack') !== -1) {
        q += 6 * qty;
        hasPack = true;
      } else {
        hasExtras = true;
      }
    });

    var valid = q > 0 && q % 6 === 0;
    var within = valid ? 6 : q % 6;
    var fraction = q === 0 ? 0 : within / 6;
    var remaining = (6 - (q % 6)) % 6;
    var target = valid ? q : Math.ceil(q / 6) * 6;
    var state = q === 0 ? 'empty' : valid ? 'valid' : q < 6 ? 'building' : 'over';

    return {
      q: q,
      valid: valid,
      within: within,
      fraction: fraction,
      remaining: remaining,
      target: target,
      hasExtras: hasExtras,
      hasPack: hasPack,
      state: state,
    };
  }

  function copyFor(s) {
    if (s.q === 0) {
      // Non-wine-only cart: reassure, never imply the six-rule blocks it.
      // Unreachable with wine in the cart (any counted wine makes q > 0).
      if (s.hasExtras) {
        return { main: NO_WINE_LINE, sub: '' };
      }
      return { main: 'We ship wine in sixes.', sub: 'Mix any bottles you like.' };
    }
    if (s.valid) {
      return { main: 'Ready to check out.', sub: 'Add another six, or check out any time.' };
    }
    // Only ever prompt to add up to the next six; never suggest removing.
    var bottleWord = s.remaining === 1 ? 'bottle' : 'bottles';
    return {
      main: 'Add ' + s.remaining + ' more ' + bottleWord + ' to check out.',
      sub: 'We ship wine in sixes, mix any bottles you like.',
    };
  }

  function liveTextFor(s) {
    if (s.state === 'empty') return 'Your cart.';
    if (s.valid) return s.q + ' bottles in your cart. Ready to check out.';
    var bottleWord = s.remaining === 1 ? 'bottle' : 'bottles';
    return s.q + ' of ' + s.target + ' bottles. Add ' + s.remaining + ' more ' + bottleWord + ' to check out.';
  }

  function setText(parent, selector, text) {
    var el = parent.querySelector(selector);
    if (el) el.textContent = text;
  }

  function renderBadge(badge, s, copy) {
    badge.dataset.state = s.state;
    badge.dataset.q = String(s.q);
    badge.dataset.target = String(s.target);
    badge.dataset.remaining = String(s.remaining);
    badge.dataset.extras = String(s.hasExtras);

    var prog = badge.querySelector('.vc-ring__prog');
    if (prog) {
      prog.style.strokeDasharray = String(RING_CIRCUMFERENCE);
      prog.style.strokeDashoffset = String(RING_CIRCUMFERENCE * (1 - s.fraction));
    }

    var frac = badge.querySelector('.vc-frac');
    if (frac) {
      frac.textContent = s.valid ? s.q + ' of ' + s.q + ' ✓' : s.q + ' of ' + s.target;
    }

    var tip = badge.querySelector('.vc-tip');
    if (tip) {
      while (tip.firstChild) tip.removeChild(tip.firstChild);
      tip.appendChild(document.createTextNode(copy.main));
      if (copy.sub) {
        var sub = document.createElement('span');
        sub.className = 'vc-tip__sub';
        sub.textContent = copy.sub;
        tip.appendChild(sub);
      }
      if (s.hasPack) {
        var pack = document.createElement('span');
        pack.className = 'vc-tip__exc vc-tip--pack';
        pack.textContent = PACK_LINE;
        tip.appendChild(pack);
      }
      if (s.hasExtras && s.q > 0) {
        var exc = document.createElement('span');
        exc.className = 'vc-tip__exc';
        exc.textContent = EXC_LINE;
        tip.appendChild(exc);
      }
    }

    var live = badge.querySelector('[data-vc-live]');
    if (live) live.textContent = liveTextFor(s);
  }

  function renderStatus(block, s, copy) {
    block.dataset.state = s.state;
    var fill = block.querySelector('.vc-six-status__fill');
    if (fill) fill.style.width = s.fraction * 100 + '%';
    setText(block, '.vc-six-status__msg', copy.main);
    var sub = block.querySelector('.vc-six-status__sub');
    if (sub) {
      sub.textContent = copy.sub;
      sub.hidden = !copy.sub;
    }
    var pack = block.querySelector('.vc-six-status--pack');
    if (pack) pack.hidden = !s.hasPack;
    var exc = block.querySelector('.vc-six-status--exc');
    if (exc) exc.hidden = !(s.hasExtras && s.q > 0);
  }

  function renderAll(items) {
    var s = computeState(items);
    var copy = copyFor(s);
    document.querySelectorAll('[data-vc-badge]').forEach(function (badge) {
      renderBadge(badge, s, copy);
    });
    document.querySelectorAll('[data-vc-status]').forEach(function (block) {
      renderStatus(block, s, copy);
    });
  }

  var pending = null;
  function refresh(cartData) {
    // Use the event's cart payload only when it is a full cart; otherwise
    // (e.g. /cart/add.js returns just the added line) re-fetch /cart.js.
    if (cartData && Array.isArray(cartData.items) && typeof cartData.item_count === 'number') {
      renderAll(cartData.items);
      return;
    }
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js')
        .then(function (res) {
          return res.json();
        })
        .then(function (cart) {
          renderAll(cart.items || []);
        })
        .catch(function () {
          /* leave the last server-rendered state in place */
        });
    }, 80);
  }

  function init() {
    // Preferred: the theme's pub/sub cart update event. constants.js declares
    // PUB_SUB_EVENTS with `const`, so it is NOT a window property - it must be
    // read as a bare global behind a typeof guard.
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
    var eventName = (events && events.cartUpdate) || 'cart-update';
    var subscribeFn = typeof subscribe === 'function' ? subscribe : window.subscribe;
    if (typeof subscribeFn === 'function') {
      subscribeFn(eventName, function (event) {
        refresh(event && event.cartData);
      });
    }

    // Belt-and-braces fallbacks, resilient across Craft updates.
    document.addEventListener('cart:updated', function () {
      refresh();
    });
    document.addEventListener('cart:refresh', function () {
      refresh();
    });

    // Re-fetch whenever the cart notification opens (its status block is
    // not section-rendered on add-to-cart).
    var notification = document.getElementById('cart-notification');
    if (notification && typeof MutationObserver !== 'undefined') {
      var wasActive = notification.classList.contains('active');
      new MutationObserver(function () {
        var isActive = notification.classList.contains('active');
        if (isActive && !wasActive) refresh();
        wasActive = isActive;
      }).observe(notification, { attributes: true, attributeFilter: ['class'] });
    }

    // Restore correct state when returning via the back/forward cache.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) refresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
